import React, {ChangeEvent, SyntheticEvent, useEffect, useState} from "react";
import localStyles from "./index.module.css";
import commonStyles from '../common/commonStyles/table.module.css';
import {SpecialGroup} from "../dashboard";
import {
    Button,
    Dropdown,
    DropdownItemProps,
    DropdownProps,
    Form, Header,
    Icon,
    Loader,
    Menu,
    Message,
    Table,
} from "semantic-ui-react";
import agent, { PaginateOptions } from "../../agent";
import { Utils } from "../../helpers/Utils";
import { AppMode, GeneralTitleCase, getGeneralTitle } from "../../utils/common";
import { hasRole, UserRole } from "../../helpers/UserRole";
import TablePage from "../general/TablePage";
import { useUrlPagination } from "../../hooks/useUrlPagination";
import {
    CommonListPagePaginationResponse,
    DEFAULT_LIMIT,
    DEFAULT_PAGE,
    DEFAULT_SORT_COLUMN,
    handleSortPagination,
    ListState,
} from "../../helpers/TablePageUtils";
import { textLabels } from "../../consts/textLabels";

export interface SpecialGroupPaginationResponse extends CommonListPagePaginationResponse<SpecialGroup> {}

interface ExtendedListState extends ListState<SpecialGroup> {
    specialGroupsIdsFilter?: string[];
}

const initialState: ExtendedListState = {
    page: DEFAULT_PAGE,
    limit: DEFAULT_LIMIT,
    sortColumn: DEFAULT_SORT_COLUMN,
    searchValue: "",
    docs: [],
    totalDocs: 0,
    totalPages: 1,
    loading: false,
    error: null,
    specialGroupsIdsFilter: undefined,
};

const SpecialGroupList: React.FC = () => {
    const currentPath = "/dashboard/specialGroups";

    const { state, setState, updateUrl } = useUrlPagination<ExtendedListState>(initialState, {
        defaultSortColumn: DEFAULT_SORT_COLUMN,
        customParams: ["specialGroupsIdsFilter"],
    });

    const [allSpecialGroups, setAllSpecialGroups] = useState<SpecialGroup[]>([]);
    const [loadingAll, setLoadingAll] = useState(false);

    const loadAllSpecialGroups = async () => {
        setLoadingAll(true);
        try {
            const response: { docs: SpecialGroup[] } = await agent.SpecialGroupProvider.getList();
            setAllSpecialGroups(response.docs);
        } catch (error) {
            console.error("Ошибка загрузки списка для фильтра:", error);
        } finally {
            setLoadingAll(false);
        }
    };

    const fetchList = async () => {
        setState(prev => ({ ...prev, loading: true, error: null }));

        const paginateOptions: PaginateOptions = {
            page: state.page,
            limit: state.limit,
            sort: state.sortColumn,
            specialGroupsIdsFilter: state.specialGroupsIdsFilter,
        };

        try {
            const response: SpecialGroupPaginationResponse = await agent.SpecialGroupProvider.getList(paginateOptions);

            if (response) {
                setState(prev => ({
                    ...prev,
                    docs: response.docs,
                    totalDocs: response.totalDocs,
                    totalPages: response.totalPages,
                    loading: false,
                    error: null,
                }));
            }
        } catch (error) {
            console.error("Ошибка получения:", error);
            setState(prev => ({
                ...prev,
                docs: [],
                totalDocs: 0,
                totalPages: 1,
                loading: false,
                error: "Не удалось загрузить список. Пожалуйста, попробуйте позже.",
            }));
        }
    };

    useEffect(() => {
        loadAllSpecialGroups();
    }, []);

    useEffect(() => {
        fetchList();
    }, [state.page, state.limit, state.sortColumn, state.specialGroupsIdsFilter]);

    useEffect(() => {
        updateUrl();
    }, [state.page, state.limit, state.sortColumn, state.specialGroupsIdsFilter]);

    const handleFilterChange = (_event: React.SyntheticEvent<HTMLElement>,  data: DropdownProps) => {
        const selectedValues = (data.value as string[]) || [];
        const specialGroupsIdsFilter = selectedValues.length > 0 ? selectedValues : undefined;
        setState(prev => ({ ...prev, specialGroupsIdsFilter, page: DEFAULT_PAGE }));
    };

    const handleSort = (column: string) => {
        const newSort = handleSortPagination(state.sortColumn, column);
        setState(prev => ({ ...prev, sortColumn: newSort, page: DEFAULT_PAGE }));
    };

    const handlePageChange = (page: number) => {
        setState(prev => ({ ...prev, page }));
    };

    const handleLimitChange = (limit: number) => {
        setState(prev => ({ ...prev, limit, page: DEFAULT_PAGE }));
    };

    const {
        docs,
        totalDocs,
        totalPages,
        loading,
        error,
        page,
        limit,
        sortColumn,
        specialGroupsIdsFilter,
    } = state;

    const filterValue = Array.isArray(specialGroupsIdsFilter)
        ? specialGroupsIdsFilter
        : (specialGroupsIdsFilter ? (specialGroupsIdsFilter as string).split(',').filter((id: string) => id.trim() !== '') : []);

    const styles = { ...commonStyles, ...localStyles };
    const headerLabel = `Список ${Utils.vomode() !== AppMode.STUDENT ? "особых групп" : "курсов"}`;
    const cellLabel = Utils.vomode() !== AppMode.STUDENT ? "особой группы" : "курса";
    const listLoaderLabel = textLabels.listLoaderMessage;
    const errorLabel = textLabels.errorMessage;

    return (
        <div className={styles.table}>
            <div className={styles.header}><Header>{headerLabel}</Header></div>

            {error && (
                <Message negative>
                    <Message.Header>{errorLabel}</Message.Header>
                    <p>{error}</p>
                </Message>
            )}

            {(loading || loadingAll) ? (
                <Loader active>{listLoaderLabel}</Loader>
            ) : (
                <div className={styles.list}>
                    <Form>
                        <Form.Group>
                            <Form.Field width={4}>
                                <Dropdown
                                    selection
                                    multiple
                                    search
                                    clearable
                                    options={allSpecialGroups.map((sg): DropdownItemProps => ({
                                        key: sg._id,
                                        text: sg.name,
                                        value: sg._id,
                                    }))}
                                    value={filterValue}
                                    onChange={handleFilterChange}
                                    noResultsMessage={textLabels.noResultsMessage}
                                />
                            </Form.Field>
                            <Form.Button primary content="Поиск" onClick={fetchList} />
                        </Form.Group>
                    </Form>

                    <div className={styles.container}>
                        <Table celled striped verticalAlign="middle" color="teal">
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell
                                        sorted={
                                            sortColumn === "name" || sortColumn === "-name"
                                                ? sortColumn.startsWith("-")
                                                    ? "descending"
                                                    : "ascending"
                                                : undefined
                                        }
                                        onClick={() => handleSort("name")}
                                    >
                                        Наименование {cellLabel}
                                    </Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>

                            <Table.Body>
                                {docs.map((el) => (
                                    <Table.Row style={{ cursor: "pointer" }} key={`special-group-${el._id}`}>
                                        <Table.Cell floated="left">{el.name}</Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>

                            <Table.Footer>
                                <Table.Row>
                                    <Table.HeaderCell colSpan="1">
                                        <Menu floated="right" pagination>
                                            <TablePage
                                                currentPage={page}
                                                totalPages={totalPages}
                                                totalDocs={totalDocs}
                                                limit={limit}
                                                onPageChange={handlePageChange}
                                                onLimitChange={handleLimitChange}
                                            />
                                        </Menu>
                                    </Table.HeaderCell>
                                </Table.Row>
                            </Table.Footer>
                        </Table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SpecialGroupList;